import React, { useState } from "react";

import { Link, useNavigate } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";

import {
  ArrowLeft,
  Mail,
  Lock,
  User,
  Phone,
  AtSign,
  MailCheck,
  ArrowRight,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import DateField from "../components/ui/DateField";

import { useAuth } from "../context/AuthContext";

import { formatApiError, api } from "../lib/api";
import AdvisorConnectModal from "../components/advisor/AdvisorConnectModal";
import { toast } from "sonner";
import SocialAuthButtons, {
  SOCIAL_AUTH_ENABLED,
} from "../components/auth/SocialAuthButtons";

import logo from "../assets/accesswealth logo.png";
import logo2 from "../assets/accesswealth logo light.png";
import { useTheme } from "@/context/ThemeContext";

// ── Email sent confirmation screen ────────────────────────────────────────────
function EmailSentCard({
  email,
  onResend,
  resending,
  onGoToLogin,
  onReRegister,
}) {
  return (
    <div className="relative w-full max-w-md animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* decorative top glow */}
      <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-0 h-64 w-64 rounded-full bg-emerald-400/20 blur-3xl" />

      <div className="card-soft p-8 text-center relative overflow-hidden">
        {/* subtle background pattern */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03] dark:opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle, #10b981 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        />

        {/* icon */}
        <div className="relative mx-auto mb-6 flex h-20 w-20 items-center justify-center">
          {/* ring pulse */}
          <span className="absolute inset-0 rounded-full bg-emerald-100 dark:bg-emerald-500/20 animate-ping opacity-40" />
          <span className="absolute inset-0 rounded-full bg-emerald-50 dark:bg-emerald-500/10" />
          <MailCheck
            className="relative h-9 w-9 text-emerald-600 dark:text-emerald-400"
            strokeWidth={1.5}
          />
        </div>

        {/* heading */}
        <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white mb-2">
          Check your inbox
        </h2>

        <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed mb-1">
          We've sent a verification link to
        </p>
        <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 break-all mb-6">
          {email}
        </p>

        <p className="text-xs text-slate-400 dark:text-slate-500 leading-relaxed mb-8">
          Click the link in the email to activate your account. If you don't see
          it, check your spam or junk folder.
        </p>

        {/* divider */}
        <div className="border-t border-slate-100 dark:border-slate-800 mb-6" />

        {/* actions */}
        <div className="space-y-3">
          <button
            type="button"
            onClick={onGoToLogin}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            Go to sign in
            <ArrowRight className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={onResend}
            disabled={resending}
            className="btn-secondary w-full flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${resending ? "animate-spin" : ""}`}
            />
            {resending ? "Resending…" : "Resend email"}
          </button>
        </div>

        <p className="mt-6 text-[11px] text-slate-400 dark:text-slate-600">
          Wrong address?{" "}
          <button
            type="button"
            onClick={onReRegister}
            className="text-emerald-600 font-medium underline underline-offset-2 cursor-pointer"
          >
            Go back and re-register
          </button>
        </p>
      </div>
    </div>
  );
}

// ── Main Register page ─────────────────────────────────────────────────────────
export default function Register() {
  const { register } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  // Optional Personal Finance Advisor referral code + confirmation popup state.
  const [advisorCode, setAdvisorCode] = useState("");
  const [advisorInfo, setAdvisorInfo] = useState(null); // looked-up advisor
  const [showAdvisorModal, setShowAdvisorModal] = useState(false);
  const [advisorChoice, setAdvisorChoice] = useState(null); // {modules, permission_level}

  // After success: show the email card instead of navigating away
  const [registered, setRegistered] = useState(false);
  const [resending, setResending] = useState(false);

  // ==================================================
  // REGISTER
  // ==================================================

  const handleRegister = async (advisorConn = advisorChoice) => {
    setError("");
    setLoading(true);

    if (phone.length < 10) {
      setError("Please enter a valid 10-digit phone number");
      setLoading(false);
      return;
    }
    if (!dob) {
      setError("Please enter your date of birth");
      setLoading(false);
      return;
    }

    try {
      await register({
        name: name.trim(),
        email: email.trim(),
        username: username.trim(),
        phone: phone.trim(),
        date_of_birth: dob,
        password,
        accept_terms: true,
        ...(advisorConn && advisorInfo
          ? {
              advisor_code: advisorInfo.advisor_code,
              advisor_permission: advisorConn.permission_level,
              advisor_modules: advisorConn.modules,
            }
          : {}),
      });

      setShowTermsModal(false);
      setShowAdvisorModal(false);
      setRegistered(true); // ← show email card
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // After accepting terms: if an advisor code was entered, look it up and show
  // the confirmation popup before connecting; otherwise register directly.
  const proceedAfterTerms = async () => {
    const code = advisorCode.trim();
    if (!code) {
      handleRegister(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/advisor-code/lookup", {
        advisor_code: code,
      });
      if (!data.found) {
        setError(
          "That advisor code wasn't recognised. Check it, or clear it to continue without an advisor.",
        );
        setLoading(false);
        return;
      }
      setAdvisorInfo(data);
      setShowTermsModal(false);
      setShowAdvisorModal(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    // Hook into whatever resend-verification API you have.
    // For now we just show a brief spinner as a placeholder.
    setResending(true);
    try {
      // await resendVerification({ email });
      await new Promise((r) => setTimeout(r, 1500)); // remove when real API is wired
    } finally {
      setResending(false);
    }
  };

  // ── Email sent state ──────────────────────────────
  if (registered) {
    return (
      <div className="min-h-screen mesh-bg flex items-center justify-center p-4">
        <EmailSentCard
          email={email}
          onResend={handleResend}
          resending={resending}
          onGoToLogin={() => navigate("/login")}
          onReRegister={() => setRegistered(false)}
        />
      </div>
    );
  }

  // ── Registration form ─────────────────────────────
  return (
    <div className="min-h-screen mesh-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* BACK */}
        <Link
          to="/"
          data-testid="register-back-home"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-emerald-600 mb-6"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to home
        </Link>

        {/* CARD */}
        <div className="card-soft p-8">
          {/* HEADER */}
          <div className="flex items-center gap-3 mb-6">
            <div className="grid h-12 w-10 place-items-center overflow-hidden">
              {theme === "dark" ? (
                <img
                  src={logo2}
                  alt="AccessWealth"
                  className="h-full w-full object-cover"
                />
              ) : (
                <img
                  src={logo}
                  alt="AccessWealth"
                  className="h-full w-full object-cover"
                />
              )}
            </div>

            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">
                Get started
              </div>
              <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white">
                Create your account
              </h1>
            </div>
          </div>

          {/* FORM */}
          <form className="space-y-4">
            {/* NAME */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Full name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  required
                  data-testid="register-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-soft !pl-10"
                  placeholder="Aarav Mehta"
                />
              </div>
            </div>

            {/* EMAIL */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="email"
                  required
                  data-testid="register-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-soft !pl-10"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            {/* USERNAME */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Username
              </label>
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  required
                  minLength={3}
                  maxLength={20}
                  data-testid="register-username"
                  value={username}
                  onChange={(e) =>
                    setUsername(e.target.value.toLowerCase().replace(/\s/g, ""))
                  }
                  className="input-soft !pl-10"
                  placeholder="AaravM"
                />
              </div>
              <p className="text-[11px] text-slate-500">
                Unique username used for family linking
              </p>
            </div>

            {/* PHONE */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Phone number
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="tel"
                  required
                  maxLength={10}
                  data-testid="register-phone"
                  value={phone}
                  onChange={(e) =>
                    setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))
                  }
                  className="input-soft !pl-10"
                  placeholder="9876543210"
                />
              </div>
              <p className="text-[11px] text-slate-500">
                Used to link family members who share with you.
              </p>
            </div>

            {/* DATE OF BIRTH */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Date of birth
              </label>
              <DateField
                data-testid="register-dob"
                value={dob}
                max={new Date().toISOString().slice(0, 10)}
                toYear={new Date().getFullYear()}
                onChange={(v) => setDob(v)}
              />
              <p className="text-[11px] text-slate-500">
                Used to project your retirement age and corpus.
              </p>
            </div>

            {/* PASSWORD */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="password"
                  required
                  minLength={6}
                  data-testid="register-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-soft !pl-10"
                  placeholder="At least 6 characters"
                />
              </div>
            </div>

            {/* ADVISOR REFERRAL CODE (optional) */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Advisor referral code{" "}
                <span className="text-slate-400">(optional)</span>
              </label>
              <div className="relative">
                <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  data-testid="register-advisor-code"
                  value={advisorCode}
                  onChange={(e) =>
                    setAdvisorCode(e.target.value.toUpperCase().trim())
                  }
                  className="input-soft !pl-10 font-mono"
                  placeholder="PFA-XXXXXXXX"
                />
              </div>
              <p className="text-[11px] text-slate-500">
                Have a Personal Finance Advisor? Enter their code to connect.
                You'll choose exactly what to share next.
              </p>
            </div>

            {/* ERROR */}
            {error && (
              <div
                data-testid="register-error"
                className="rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-100 dark:border-rose-500/20 text-rose-700 dark:text-rose-300 text-xs p-3"
              >
                {error}
              </div>
            )}

            {/* SUBMIT */}
            <button
              type="button"
              data-testid="register-submit"
              onClick={() => {
                setError("");
                setShowTermsModal(true);
              }}
              className="btn-primary w-full"
            >
              Create account
            </button>
          </form>

          {/* SOCIAL SIGN-UP */}
          {SOCIAL_AUTH_ENABLED && (
            <>
              <div className="my-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
                <span className="text-[10px] uppercase tracking-widest text-slate-400">
                  or sign up with
                </span>
                <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
              </div>
              <SocialAuthButtons />
              <p className="mt-3 text-[10px] text-center text-slate-400">
                By continuing with Google or Apple you agree to our Terms &
                Privacy Policy.
              </p>
            </>
          )}

          {/* FOOTER */}
          <p className="mt-6 text-xs text-center text-slate-500">
            Already have an account?{" "}
            <Link
              to="/login"
              data-testid="register-to-login"
              className="text-emerald-600 font-semibold"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* TERMS MODAL */}
      {showTermsModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-2xl rounded-3xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            {/* HEADER */}
            <div className="p-6 border-b border-slate-200 dark:border-slate-800">
              <h2 className="font-display text-2xl font-bold">
                Terms & Privacy
              </h2>
              <p className="mt-2 text-sm text-slate-500">
                Please review and accept before creating your account.
              </p>
            </div>

            {/* BODY */}
            <div className="max-h-[60vh] overflow-y-auto p-6 space-y-6 text-sm text-slate-600 dark:text-slate-300">
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                  Important Notice
                </h3>
                <p>
                  AccessWealth helps users organize financial and inheritance
                  information. Users are fully responsible for:
                </p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>Providing correct nominee information</li>
                  <li>Managing account security</li>
                  <li>Choosing trusted family members</li>
                  <li>Verifying linked phone numbers and email addresses</li>
                  <li>Maintaining accurate inheritance allocations</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                  Family Linking Disclaimer
                </h3>
                <p>
                  AccessWealth may automatically link family members using
                  matching email addresses or phone numbers. AccessWealth is not
                  responsible for incorrect linking caused by inaccurate
                  information entered by users.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                  Data & Security
                </h3>
                <p>
                  While AccessWealth follows strong security practices, no
                  online platform can guarantee absolute security. Users
                  understand and accept the inherent risks of digital storage
                  and online access.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                  Financial & Legal Disclaimer
                </h3>
                <p>
                  AccessWealth does not provide legal, tax, investment, or
                  financial advice. Users should consult qualified professionals
                  before making financial or inheritance decisions.
                </p>
              </div>

              <div className="rounded-2xl bg-slate-50 dark:bg-slate-800 p-4 border border-slate-200 dark:border-slate-700">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={acceptTerms}
                    onCheckedChange={(checked) =>
                      setAcceptTerms(checked === true)
                    }
                    className="mt-1"
                  />
                  <span className="text-sm">
                    I agree to the Terms of Service and Privacy Policy, and
                    understand the risks and responsibilities of storing and
                    sharing financial continuity information.
                  </span>
                </label>
              </div>
            </div>

            {/* MODAL ERROR */}
            {error && (
              <div className="mx-6 mb-4 rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-100 dark:border-rose-500/20 text-rose-700 dark:text-rose-300 text-xs p-3">
                {error}
              </div>
            )}

            {/* FOOTER */}
            <div className="p-6 border-t border-slate-200 dark:border-slate-800 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setError("");
                  setShowTermsModal(false);
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={!acceptTerms || loading}
                onClick={proceedAfterTerms}
                className="btn-primary flex-1 disabled:opacity-50"
              >
                {loading
                  ? "Please wait…"
                  : advisorCode.trim()
                    ? "Continue"
                    : "Accept & Create Account"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADVISOR CONNECT CONFIRMATION POPUP */}
      {showAdvisorModal && advisorInfo && (
        <AdvisorConnectModal
          advisor={advisorInfo}
          mode="simple"
          confirmLabel="Confirm & create account"
          onCancel={() => {
            setShowAdvisorModal(false);
            setShowTermsModal(true);
          }}
          onConfirm={async (choice) => {
            setAdvisorChoice(choice);
            await handleRegister(choice);
          }}
        />
      )}
    </div>
  );
}
